import type { components, paths } from "@/api-schema";
import { PathsV1VehicleUuidGetParametersQueryExtend, RelocationOrderStatus } from "@/api-schema-enums";
import { useRelocationOrderCreateMutation } from "@/common/api/relocation-order/use-relocation-order-create.mutation";
import { useVehicleByUuidQuery } from "@/common/api/vehicle/use-vehicle-by-uuid.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { isAddressFormEmpty } from "@/components/address-form-fields/_helpers/address-form-values";
import { addressFormValuesToAddressDto } from "@/components/address-form-fields/_helpers/address-form-values-to-address-dto";
import { addressFormDefaultValues, addressFormSchema } from "@/components/address-form-fields/address-form-schema";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { RelocationOrderAdminForm } from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form";
import {
  buildRelocationOrderAdminFormSchema,
  RelocationOrderAdminFormSchemaType,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form-fields";
import { sanitizeRelocationOrderValues } from "@/modules/fit/internal/relocation-order/admin/_helpers/sanitize-relocation-order-values";
import { useRelocationOrderAdminFormState } from "@/modules/fit/internal/relocation-order/admin/_hooks/use-relocation-order-admin-form-state";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useSearch } from "wouter";
import { navigate } from "wouter/use-browser-location";

type Vehicle = components["schemas"]["Vehicle"];

const SELECT_VEHICLE_PATH = "~/fit/relocation-order/create/select-vehicle";

export function AdminCreateView() {
  const [_, setLocation] = useLocation();
  const vehicleUuid = new URLSearchParams(useSearch()).get("vehicleUuid");

  useEffect(() => {
    if (vehicleUuid) return;

    setLocation(SELECT_VEHICLE_PATH, { replace: true });
  }, [setLocation, vehicleUuid]);

  if (!vehicleUuid) {
    return null;
  }

  return <AdminCreateContent key={vehicleUuid} vehicleUuid={vehicleUuid} />;
}

function AdminCreateContent({ vehicleUuid }: { vehicleUuid: string }) {
  const { t } = useTranslation(["common", "fit"]);
  const vehicleQuery = useVehicleByUuidQuery(vehicleUuid, {
    parameters: {
      extend: [
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily,
        PathsV1VehicleUuidGetParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Address,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Company,
        PathsV1VehicleUuidGetParametersQueryExtend.CurrentBranch_Brands,
      ],
    },
  });

  return (
    <UuidExistenceWrapper<
      paths["/v1/vehicle/{uuid}"]["get"]["parameters"]["query"],
      paths["/v1/vehicle/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
    >
      uuid={vehicleUuid}
      query={useVehicleByUuidQuery}
      queryResult={vehicleQuery}
      notFoundProps={{
        title: t("fit:VehicleNotFound"),
        message: t("fit:RequestedVehicleDoesNotExist"),
        description: t("fit:CheckVehicleIdOrContactSupport"),
        backTo: SELECT_VEHICLE_PATH,
        backToLabel: t("fit:BackToVehicleSelection"),
      }}
    >
      {vehicleQuery.data && (
        <CenteredLayout
          variant={CenteredLayoutVariant.Form}
          title={t("fit:AdminCreate")}
          subTitle={t("common:CompleteRequiredInformationDescription")}
        >
          <AdminCreateForm vehicle={vehicleQuery.data} />
        </CenteredLayout>
      )}
    </UuidExistenceWrapper>
  );
}

function AdminCreateForm({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useTranslation(["common", "fit"]);
  const { isPending: relocationOrderCreateIsLoading, mutateAsync: relocationOrderCreateMutateAsync } =
    useRelocationOrderCreateMutation();

  const form = useForm<RelocationOrderAdminFormSchemaType>({
    mode: "onChange",
    resolver: zodResolver(buildRelocationOrderAdminFormSchema()),
    defaultValues: {
      originBranch: vehicle.currentBranch?.uuid ?? "",
      originAddress: { ...addressFormDefaultValues },
      currentBranch: vehicle.currentBranch?.uuid ?? "",
      currentAddress: { ...addressFormDefaultValues },
      destinationBranch: "",
      destinationAddress: { ...addressFormDefaultValues },
      earliestPickupDate: "",
      pickedUpBy: null,
      orderedBy: null,
      status: RelocationOrderStatus.TransportCompanyAwaitingPickupDate,
      transportCompany: null,
      transportMethod: undefined,
      transportReason: undefined,
      vehicle: vehicle.uuid,
    },
  });

  const { options, isLoading: isOptionsLoading } = useRelocationOrderAdminFormState({
    form,
    vehicle,
  });

  const onSubmit = async (values: RelocationOrderAdminFormSchemaType) => {
    const sanitized = sanitizeRelocationOrderValues(values);
    const hasOriginAddress = !isAddressFormEmpty(sanitized.originAddress);
    const hasCurrentAddress = !isAddressFormEmpty(sanitized.currentAddress);
    const hasDestinationAddress = !isAddressFormEmpty(sanitized.destinationAddress);
    const originAddress = hasOriginAddress
      ? addressFormValuesToAddressDto(addressFormSchema.parse(sanitized.originAddress))
      : undefined;
    const currentAddress = hasCurrentAddress
      ? addressFormValuesToAddressDto(addressFormSchema.parse(sanitized.currentAddress))
      : undefined;
    const destinationAddress = hasDestinationAddress
      ? addressFormValuesToAddressDto(addressFormSchema.parse(sanitized.destinationAddress))
      : undefined;

    const payload = {
      currentAddress,
      currentBranch: sanitized.currentBranch || undefined,
      originBranch: sanitized.originBranch || undefined,
      originAddress,
      destinationBranch: sanitized.destinationBranch || undefined,
      destinationAddress,
      earliestPickupDate: sanitized.earliestPickupDate,
      pickedUpBy: sanitized.pickedUpBy,
      orderedBy: sanitized.orderedBy,
      status: sanitized.status,
      transportCompany: sanitized.transportCompany,
      transportMethod: sanitized.transportMethod,
      transportReason: sanitized.transportReason,
      vehicle: sanitized.vehicle,
    };

    await toastMutationStatus({
      mutationFn: () => relocationOrderCreateMutateAsync(payload),
      successMessage: t("fit:RelocationOrderCreated"),
      errorMessage: t("fit:ErrorCreatingOrder"),
      customErrors: {
        409: null,
      },
    });

    navigate("/fit/relocation-order");
  };

  return (
    <RelocationOrderAdminForm
      cardTitle={t("fit:AdminCreate")}
      vehicle={vehicle}
      form={form}
      onSubmit={onSubmit}
      isLoading={relocationOrderCreateIsLoading || isOptionsLoading}
      options={options}
    />
  );
}
